import type { QueryRowType } from "@rocicorp/zero";
import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { nanoid } from "nanoid";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { type EffortLevel } from "../../shared/aiProviders";
import { sharedMutators } from "../../shared/mutators";
import { queries, roundedHoursAgo } from "../../shared/queries";
import { config } from "../environment";
import { useComposerStrip } from "../hooks/useComposerStrip";
import { useCreateSkill } from "../hooks/useCreateSkill";
import { useFileDropzone } from "../hooks/useFileDropzone";
import { useModelExperiment } from "../hooks/useModelExperiment";
import { useTenantDefaultProviderId } from "../hooks/useScopedModel";
import { useFocusShortcut } from "../hooks/useFocusShortcut";
import { useGetPresence } from "../hooks/useGetPresence";
import { useIsMobile } from "../hooks/useIsMobile";
import { usePlanModeShortcut } from "../hooks/usePlanModeShortcut";
import { useQuery } from "../hooks/useQuery";
import { useSlashSkills } from "../hooks/useSlashSkills";
import { useSpaceDisplayName } from "../hooks/useSpaceDisplayName";
import { useUrlSort } from "../hooks/useUrlSort";
import { isSpaceMember, useSpaces } from "../hooks/useSpaces";
import { useWorkflowZipImport } from "../hooks/useWorkflowZipImport";
import { prependAttachedSkills } from "../lib/attachedSkills";
import { connectorAvatarItems, mainBranchConnectorIds } from "../lib/connector";
import { useCaretToEndOnMount, useDraft } from "../lib/draft";
import { formatElapsedTime } from "../lib/formatElapsedTime";
import { resolveDefaultPlanMode, savePlanMode } from "../lib/planMode";
import {
  makeUrl,
  navigate,
  routes,
  useCurrentUrl,
  useRouteMatch,
} from "../lib/urls";
import { userDisplayName } from "../lib/userDisplayName";
import {
  type TriggerType,
  triggerTypeOrder,
  workflowTriggerTypes,
} from "../lib/workflowTrigger";
import { ActivityFacepile } from "./ActivityFacepile";
import { AnimatedPlaceholder } from "./AnimatedPlaceholder";
import {
  AttachmentPreview,
  AttachmentPreviewRow,
  AttachmentRowAnimator,
  Dropzone,
  ImportError,
  marchingDashes,
} from "./AttachmentPreviews";
import { Backdrop } from "./Backdrop";
import {
  BuildStatus,
  getBranchListStatus,
  type StatusFilterValue,
  statusFilterOrder,
} from "./BuildStatus";
import { Button } from "./Button";
import { ComposerStrip } from "./Chat/ComposerStrip";
import { ComposerStripToggle } from "./Chat/ComposerStripToggle";
import { GhostText } from "./Chat/GhostText";
import { SlashMenu } from "./Chat/SlashMenu";
import { gettingStartedSuggestions } from "./Chat/workflowSuggestions";
import { CircleMesh, shuffleColors } from "./CircleMesh";
import { ConnectorAvatars } from "./ConnectorAvatars";
import { DismissibleHeroLayout } from "./DismissibleHeroLayout";
import { EmptyState } from "./EmptyState";
import { FlyingDot } from "./FlyingDot";
import { Icon } from "./Icon";
import {
  InputContainer,
  WORKFLOW_INPUT_HEIGHT,
  WORKFLOW_INPUT_WIDTH,
} from "./InputContainer";
import { TriggerTypes } from "./TriggerTypes";
import {
  hasActiveFilters,
  ListFilter,
  ListFilterPills,
  parseMultiFilter,
} from "./ListFilter";
import { ListTable } from "./ListTable";
import { countPageCards, PagesList } from "./PagesList";
import { PagesTabHeader } from "./PagesTabHeader";
import { PlanModeToggle } from "./PlanModeToggle";
import { SpaceBadge } from "./SpaceBadge";
import { SpaceDropdown } from "./SpaceDropdown";
import { StatusIndicator } from "./StatusIndicator";
import { Text } from "./Text";
import { Tooltip } from "./Tooltip";
import { VoiceRecorder } from "./VoiceRecorder";
import { WorkflowActionsMenu } from "./WorkflowActionsMenu";
import {
  workflowListFilters,
  workflowListSortColumns,
} from "./workflowListFilters";
import { WorkflowNameCell } from "./WorkflowNameCell";

// The form is anchored this far below the hero top and grows downward; the hero is
// sized to keep an equal gap below, so the top never shifts as the input expands.
const HERO_MIN_GAP = 50;

const CreateContainer = styled("div")`
  height: 100%;
  width: 100%;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: ${HERO_MIN_GAP}px;
  padding-bottom: 16px;
  box-sizing: border-box;
  z-index: 0;
  flex-shrink: 0;
  transform: translate3d(0, calc(var(--panel-progress, 0) * -16px), 0);
  will-change: transform;
`;

const CircleMeshContainer = styled("div")`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: -1;
  overflow: hidden;
  transform-origin: center bottom;
  transform: translate3d(0, calc(var(--panel-progress, 0) * 120px), 0)
    scale(calc(1 + var(--overscroll, 0) * 0.03));
  will-change: transform;
`;

const Form = styled("form")`
  padding: 4px;
  gap: 4px;
  border-radius: 16px;
  background: var(--alpha24-invert);
  backdrop-filter: blur(12px);
  display: flex;
  flex-direction: column;
  box-shadow: 0 0 0 0.5px var(--border-alpha);
  opacity: calc(1 - var(--panel-progress, 0) * 1.4);
  transform: translate3d(
    0,
    calc(var(--panel-progress, 0) * 120px - var(--overscroll, 0) * 10px),
    0
  );
  will-change: opacity, transform;
  @media (max-width: 768px) {
    width: calc(100% - 16px);
  }
`;

const HeadingRow = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 32px;
`;

const Heading = styled("div")`
  display: flex;
  align-items: center;
  height: 16px;
  box-sizing: content-box;
  gap: 6px;
  padding: 8px;
`;

const RecordingIndicator = styled("div")`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 6px;
  margin-right: 8px;
  background: var(--alpha4);
  border-radius: 50px;
`;

const RecordingDot = styled("div")`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--red-600);
`;

const VoiceRecorderContainer = styled(InputContainer)`
  width: ${WORKFLOW_INPUT_WIDTH}px;
  height: ${WORKFLOW_INPUT_HEIGHT}px;
  box-sizing: border-box;
  padding: 0;
  background: var(--alpha80-invert);

  > canvas {
    position: absolute;
    top: 12px;
    left: 12px;
    width: calc(100% - 24px) !important;
    height: calc(100% - 48px) !important;
  }
`;

type RecentPresence = QueryRowType<
  ReturnType<typeof queries.userRecentWorkflows>
>;
type RecentWorkflow = NonNullable<RecentPresence["workflow"]>;
type RecentSpace = NonNullable<RecentWorkflow["space"]>;
type RecentCreator = NonNullable<RecentWorkflow["createdBy"]>;
type ConnectorInfo = QueryRowType<
  ReturnType<typeof queries.connectedAppsIndex>
>;

function getWorkflowConnectors(
  workflow: Pick<RecentWorkflow, "branches">
): string[] {
  return mainBranchConnectorIds(workflow.branches);
}

const PROMPT_MAX_HEIGHT = 360;
// Overflowing text fades out before reaching the control rail (mirrors the chat
// composer): fully hidden over the bottom PROMPT_FADE_END px with an 8px fade above,
// and padded clear of the rail so the last line stays legible.
const PROMPT_TEXT_PADDING_BOTTOM = 44;
const PROMPT_FADE_END = 40;

const TextareaWrapper = styled(InputContainer)`
  position: relative;
  width: ${WORKFLOW_INPUT_WIDTH}px;
  min-height: ${WORKFLOW_INPUT_HEIGHT}px;
  box-sizing: border-box;
  padding: 0;
  background: var(--alpha80-invert);
  transition: background 0s linear 0.12s;

  /* Opaque while the strip is open so the panel tucked behind it doesn't bleed
     through the translucent input; timing mirrors the panel to avoid mid-animation bleed. */
  &[data-strip-open="true"] {
    background: var(--surface-primary);
    transition: background 0s linear 0s;
  }
`;

const PromptTextarea = styled("textarea")`
  display: block;
  box-sizing: border-box;
  width: 100%;
  field-sizing: content;
  min-height: ${WORKFLOW_INPUT_HEIGHT}px;
  max-height: ${PROMPT_MAX_HEIGHT}px;
  padding: 12px 12px ${PROMPT_TEXT_PADDING_BOTTOM}px;
  background: transparent;
  color: var(--text-primary);
  border: 0;
  outline: 0;
  resize: none;
  font-size: 12px;
  font-family: InterVariable;
  line-height: 16px;
  overflow-y: auto;
  /* Fade overflowing text out above the control rail only once content scrolls. */
  &[data-overflow="true"] {
    -webkit-mask-image: linear-gradient(
      to bottom,
      black calc(100% - ${PROMPT_FADE_END + 8}px),
      transparent calc(100% - ${PROMPT_FADE_END}px)
    );
    mask-image: linear-gradient(
      to bottom,
      black calc(100% - ${PROMPT_FADE_END + 8}px),
      transparent calc(100% - ${PROMPT_FADE_END}px)
    );
  }
  &::placeholder {
    color: var(--text-primary);
    opacity: 0.3;
  }
`;

const PromptControls = styled("div")`
  position: absolute;
  left: 8px;
  right: 8px;
  bottom: 8px;
  z-index: 2;
  display: flex;
  justify-content: space-between;
  align-items: center;
  pointer-events: none;
`;

const PromptControlGroup = styled("div")`
  display: flex;
  gap: 4px;
  align-items: center;
  pointer-events: auto;
`;

const ComposerColumn = styled("div")`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 4px;
  isolation: isolate;
  width: ${WORKFLOW_INPUT_WIDTH}px;
`;

export function Recents() {
  const z = useZero();
  const isMobile = useIsMobile();
  const { open: composerStripOpen } = useComposerStrip();
  const activitySince = roundedHoursAgo(24);
  const [allConnectors] = useQuery(queries.connectedAppsIndex());
  const [recentPresences, recentPresencesResult] = useQuery(
    queries.userRecentWorkflows({
      activitySince,
    })
  );
  const recentPages = recentPresences;
  const recentPagesResult = recentPresencesResult;
  const [isLinksTab] = useRouteMatch(routes.recents.links);
  const [allSpaces] = useSpaces({ settingsView: false });
  const spaces = useMemo(() => allSpaces.filter(isSpaceMember), [allSpaces]);
  const [preferences] = useQuery(queries.userPreferences());
  const [meshSeed] = useState(() => nanoid());
  const [meshColors] = useState(() => shuffleColors());
  const displayName = useSpaceDisplayName();

  const connectorById = useMemo(
    () => new Map(allConnectors.map((a) => [a.id, a])),
    [allConnectors]
  );
  const backingAppByWorkflowId = useMemo(
    () =>
      new Map(
        allConnectors
          .filter((a) => a.backingWorkflowId && !a.fresh)
          .map((a) => [a.backingWorkflowId!, a])
      ),
    [allConnectors]
  );

  const { presentByWorkflow: presentUserIdsByWorkflow } = useGetPresence({
    enabled: recentPresencesResult.type === "complete",
  });

  const [planMode, setPlanMode] = useState(false);
  const planModeTouchedRef = useRef(false);
  const [effort, setEffort] = useState<EffortLevel | null>(null);
  const [chatModel, setChatModel] = useState<string | null>(null);
  // Generated up front so the experiment model for the new thread is known
  // (and shown in the picker) before the workflow is created.
  const [draftBranchId, setDraftBranchId] = useState(() => nanoid(12));
  const experimentModel = useModelExperiment(draftBranchId);
  const effectiveChatModel = chatModel ?? experimentModel;
  const defaultPlanMode = resolveDefaultPlanMode(preferences, "workflow");
  const togglePlanMode = useCallback(() => {
    planModeTouchedRef.current = true;
    setPlanMode((on) => !on);
  }, []);
  usePlanModeShortcut(togglePlanMode);
  const [selectedSpaceId, setSelectedSpaceId] = useState<string | null>(null);
  const [prompt, setPrompt, clearPrompt] = useDraft("new-workflow");
  const [isRecording, setIsRecording] = useState(false);
  const { sortColumn, sortDirection, handleSort, sortableColumns } = useUrlSort(
    routes.recents,
    workflowListSortColumns
  );
  const {
    status: statusParam,
    space: spaceParam,
    app: appParam,
    creator: creatorParam,
    trigger: triggerParam,
  } = useCurrentUrl(routes.recents);
  const statusFilter: StatusFilterValue | null = (
    statusFilterOrder as readonly string[]
  ).includes(statusParam)
    ? (statusParam as StatusFilterValue)
    : null;
  const triggerFilters = parseMultiFilter(triggerParam);
  const spaceFilters = parseMultiFilter(spaceParam);
  const appFilters = parseMultiFilter(appParam);
  const creatorFilters = parseMultiFilter(creatorParam);
  const [recordingStartTime, setRecordingStartTime] = useState<number | null>(
    null
  );
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isTextareaFocused, setIsTextareaFocused] = useState(true);
  const textareaRef = useFocusShortcut<HTMLTextAreaElement>();
  useCaretToEndOnMount(textareaRef);
  const scrollPromptToEnd = useCallback(() => {
    const el = textareaRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [textareaRef]);
  const [promptOverflow, setPromptOverflow] = useState(false);
  const updatePromptOverflow = useCallback(() => {
    const el = textareaRef.current;
    setPromptOverflow(!!el && el.scrollHeight > el.clientHeight + 1);
  }, [textareaRef]);
  const {
    availableSkills,
    selectedSkills,
    addSkill,
    removeSkill,
    clearSkills,
    syncSlashToken,
    slashCaret,
    slashGhost,
    handleSlashKeyDown,
  } = useSlashSkills({
    spaceId: selectedSpaceId,
    textareaRef,
    value: prompt,
    setValue: setPrompt,
    onAfterAccept: () => {
      scrollPromptToEnd();
      updatePromptOverflow();
    },
    enabled: recentPresencesResult.type === "complete",
  });
  const { canCreate: canCreateSkill, createSkill } = useCreateSkill(
    selectedSpaceId ?? undefined
  );
  const [showSuggestions, setShowSuggestions] = useState(false);
  const formRef = useRef<HTMLFormElement | null>(null);
  const baseHeroHeight = isMobile ? 160 : 240;
  const [heroHeight, setHeroHeight] = useState(baseHeroHeight);
  const measureHero = useCallback(() => {
    const el = formRef.current;
    if (el) setHeroHeight(el.offsetHeight + HERO_MIN_GAP * 2);
  }, []);
  useLayoutEffect(() => {
    const el = formRef.current;
    if (!el) return;
    measureHero();
    const ro = new ResizeObserver(measureHero);
    ro.observe(el);
    return () => ro.disconnect();
  }, [measureHero]);
  // The slash menu resizes the form in one jump; re-measure synchronously so the hero keeps pace and the input's top doesn't shift.
  useLayoutEffect(() => {
    measureHero();
  }, [showSuggestions, measureHero]);
  const placeholderPromptRef = useRef("");
  const modelProviderId = useTenantDefaultProviderId({
    enabled: recentPresencesResult.type === "complete",
    chatModel: effectiveChatModel,
  });
  const { importError, importWorkflowZip } =
    useWorkflowZipImport(selectedSpaceId);
  const {
    pendingAttachments,
    isDragging,
    flyingFiles,
    processFiles,
    removeAttachment,
    clearAttachments,
    removeFlyingFile,
    handlePaste,
  } = useFileDropzone({
    providerId: modelProviderId,
    onZipFile: importWorkflowZip,
  });

  const handlePlaceholderPromptChange = useCallback((text: string) => {
    placeholderPromptRef.current = text;
  }, []);

  useEffect(() => {
    if (!planModeTouchedRef.current) setPlanMode(defaultPlanMode);
  }, [defaultPlanMode]);

  const [personalSpace] = useQuery(queries.personalSpace());
  const personalSpaceId = personalSpace?.id ?? null;
  useEffect(() => {
    if (selectedSpaceId || spaces.length === 0) return;
    const defaultId = preferences?.defaultSpace;
    const preferred = spaces.find((s) => s.id === defaultId);
    const personal = spaces.find((s) => s.id === personalSpaceId);
    setSelectedSpaceId(preferred?.id ?? personal?.id ?? spaces[0]?.id ?? null);
  }, [spaces, selectedSpaceId, preferences?.defaultSpace, personalSpaceId]);

  // Focus textarea on mount (skip on mobile to avoid keyboard auto-show)
  /* oxlint-disable react-hooks/exhaustive-deps -- intentionally runs once on mount */
  useEffect(() => {
    if (!isMobile) textareaRef.current?.focus();
  }, []);
  /* oxlint-enable react-hooks/exhaustive-deps */

  useEffect(() => {
    if (!recordingStartTime) return;
    const interval = setInterval(
      () => setElapsedTime(Date.now() - recordingStartTime),
      1000
    );
    return () => clearInterval(interval);
  }, [recordingStartTime]);

  // Filter out presences without valid workflow data
  const recentWorkflows = recentPresences
    .filter((p) => p.workflow)
    .map((p) => ({
      presence: p,
      workflow: p.workflow!,
      space: p.workflow!.space!,
    }));

  const getRecentBuildStatus = (item: (typeof recentWorkflows)[number]) => {
    const branch = item.workflow.branches[0];
    if (!branch || branch.name === "main") return null;
    return getBranchListStatus(branch, item.presence.lastSeenAt);
  };

  const availableStatuses = new Set<StatusFilterValue>();
  const availableSpaces = new Map<string, RecentSpace>();
  const availableAppIds = new Set<string>();
  const availableCreators = new Map<string, RecentCreator>();
  const availableTriggerTypes = new Set<TriggerType>();
  for (const item of recentWorkflows) {
    const status = getRecentBuildStatus(item);
    if (status) availableStatuses.add(status);
    for (const type of workflowTriggerTypes(item.workflow.branches)) {
      availableTriggerTypes.add(type);
    }
    if (item.space) availableSpaces.set(item.space.id, item.space);
    for (const appId of getWorkflowConnectors(item.workflow)) {
      availableAppIds.add(appId);
    }
    if (item.workflow.createdByUserId && item.workflow.createdBy) {
      availableCreators.set(
        item.workflow.createdByUserId,
        item.workflow.createdBy
      );
    }
  }
  const availableStatusOptions = statusFilterOrder.filter((s) =>
    availableStatuses.has(s)
  );
  const availableTriggerOptions = triggerTypeOrder.filter((t) =>
    availableTriggerTypes.has(t)
  );
  const availableSpaceOptions = [...availableSpaces.values()].sort((a, b) =>
    displayName(a).localeCompare(displayName(b))
  );
  const availableApps = [...availableAppIds]
    .map((id) => connectorById.get(id))
    .filter((a): a is ConnectorInfo => a != null)
    .sort((a, b) => a.name.localeCompare(b.name));
  const availableCreatorOptions = [...availableCreators.values()].sort((a, b) =>
    userDisplayName(a).localeCompare(userDisplayName(b))
  );

  const filteredWorkflows = recentWorkflows.filter((item) => {
    if (statusFilter !== null) {
      if (getRecentBuildStatus(item) !== statusFilter) return false;
    }
    if (
      triggerFilters.length > 0 &&
      !workflowTriggerTypes(item.workflow.branches).some((t) =>
        triggerFilters.includes(t)
      )
    )
      return false;
    if (spaceFilters.length > 0) {
      if (!item.space || !spaceFilters.includes(item.space.id)) return false;
    }
    if (appFilters.length > 0) {
      const ids = getWorkflowConnectors(item.workflow);
      if (!appFilters.some((id) => ids.includes(id))) return false;
    }
    if (creatorFilters.length > 0) {
      if (
        !item.workflow.createdByUserId ||
        !creatorFilters.includes(item.workflow.createdByUserId)
      )
        return false;
    }
    return true;
  });

  const filters = workflowListFilters({
    status: { options: availableStatusOptions, value: statusFilter },
    trigger: { options: availableTriggerOptions, values: triggerFilters },
    space: {
      options: availableSpaceOptions,
      values: spaceFilters,
      displayName,
    },
    app: { options: availableApps, values: appFilters },
    creator: { options: availableCreatorOptions, values: creatorFilters },
  });

  const sortedWorkflows = [...filteredWorkflows].sort((a, b) => {
    if (!sortColumn) {
      const aTime = a.workflow.updatedAt;
      const bTime = b.workflow.updatedAt;
      return bTime - aTime;
    }

    let aVal: string | number;
    let bVal: string | number;

    switch (sortColumn) {
      case "name":
        aVal = a.workflow.name.toLowerCase();
        bVal = b.workflow.name.toLowerCase();
        break;
      case "space":
        aVal = displayName(a.space).toLowerCase();
        bVal = displayName(b.space).toLowerCase();
        break;
      case "createdAt":
        aVal = a.workflow.createdAt;
        bVal = b.workflow.createdAt;
        break;
      case "buildStatus": {
        const getStatusVal = (w: typeof a) => {
          const branch = w.workflow.branches[0];
          if (!branch || branch.name === "main") return 0;
          const status = getBranchListStatus(branch, w.presence.lastSeenAt);
          const priority: Record<string, number> = {
            waiting: 3,
            ready: 2,
            building: 1,
            thinking: 1,
          };
          return (
            (priority[status ?? ""] ?? 0) * 1e15 + (branch.lastChatEntryAt ?? 0)
          );
        };
        aVal = getStatusVal(a);
        bVal = getStatusVal(b);
        break;
      }
      case "createdBy":
        aVal = (
          a.workflow.createdBy ? userDisplayName(a.workflow.createdBy) : ""
        ).toLowerCase();
        bVal = (
          b.workflow.createdBy ? userDisplayName(b.workflow.createdBy) : ""
        ).toLowerCase();
        break;
      default:
        return 0;
    }

    if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
    if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  const displayedWorkflows = sortedWorkflows;

  const pagesRows = useMemo(() => {
    const seen = new Set<string>();
    const out: {
      workflow: NonNullable<(typeof recentPages)[number]["workflow"]>;
      space: NonNullable<
        NonNullable<(typeof recentPages)[number]["workflow"]>["space"]
      >;
      lastSeenAt: number;
    }[] = [];
    for (const p of recentPages) {
      const w = p.workflow;
      const s = w?.space;
      if (!w || !s || seen.has(w.id)) continue;
      if (!w.branches.some((b) => (b.headCommit?.routes?.length ?? 0) > 0))
        continue;
      seen.add(w.id);
      out.push({ workflow: w, space: s, lastSeenAt: p.lastSeenAt });
    }
    return out;
  }, [recentPages]);

  const hasBuildStatus = displayedWorkflows.some(({ workflow, presence }) => {
    const branch = workflow.branches[0];
    if (!branch || branch.name === "main") return false;
    return getBranchListStatus(branch, presence.lastSeenAt) != null;
  });

  const createWorkflow = (text: string, isDictated?: boolean) => {
    if (!selectedSpaceId) return;
    const hasText = !!text.trim();
    const readyAttachments = pendingAttachments.filter((a) => a.data != null);
    const hasAttachments = readyAttachments.length > 0;
    const hasSkills = selectedSkills.length > 0;
    if (!hasText && !hasAttachments && !hasSkills) return;

    const attachments = hasAttachments
      ? readyAttachments.map(
          ({ previewUrl: _, ...rest }) =>
            rest as Omit<typeof rest, "previewUrl"> & { data: string }
        )
      : undefined;

    const workflowId = nanoid(12);
    const branchId = draftBranchId;
    setDraftBranchId(nanoid(12));
    const commitId = nanoid(12);
    const chatId = nanoid(12);
    savePlanMode(chatId, planMode);
    z.mutate(
      sharedMutators.createWorkflow({
        spaceId: selectedSpaceId,
        id: workflowId,
        branchId,
        commitId,
        chatId,
        userEntryId: nanoid(12),
        name: "Untitled",
        prompt: prependAttachedSkills(
          text.trim() || (hasAttachments ? "See attached file" : ""),
          selectedSkills
        ),
        isDictated,
        planMode,
        effort: effort ?? undefined,
        chatModel: effectiveChatModel ?? undefined,
        attachments,
      })
    );
    clearAttachments();
    clearSkills();
    clearPrompt();
    navigate(routes.space.workflow, {
      spaceId: selectedSpaceId,
      workflowId,
      branchId,
    });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    createWorkflow(prompt);
  };

  return (
    <>
      <title>{`Recents • ${config.title}`}</title>
      <Backdrop permanentNavigation>
        <DismissibleHeroLayout
          heroHeight={heroHeight}
          hero={
            <CreateContainer>
              <CircleMeshContainer
                style={{
                  height: `calc(var(--hero-height, ${baseHeroHeight}px) + 120px)`,
                }}
              >
                <CircleMesh
                  seed={meshSeed}
                  targetCircleSize={95}
                  mutingIntensity={0.6}
                  weights={[400, 600]}
                  restrictedColors={meshColors}
                />
              </CircleMeshContainer>

              <Form ref={formRef} onSubmit={handleSubmit}>
                <HeadingRow>
                  <Heading>
                    <StatusIndicator
                      state={isDragging ? "dragging" : "resting"}
                      focused={
                        isDragging ||
                        isTextareaFocused ||
                        isRecording ||
                        planMode
                      }
                      planMode={planMode}
                    />
                    <Text
                      variant="text-small-md"
                      color="var(--text-primary)"
                      style={{
                        opacity:
                          isDragging ||
                          isRecording ||
                          isTextareaFocused ||
                          planMode
                            ? 1
                            : 0.2,
                      }}
                    >
                      {isDragging
                        ? "Let’s take a look"
                        : isRecording
                          ? "Listening…"
                          : planMode
                            ? "What should we plan?"
                            : "What do you want to do?"}
                    </Text>
                  </Heading>
                  {isRecording ? (
                    <RecordingIndicator>
                      <RecordingDot />
                      <Text
                        variant="text-small-md"
                        color="var(--text-secondary)"
                      >
                        {formatElapsedTime(elapsedTime)}
                      </Text>
                    </RecordingIndicator>
                  ) : (
                    <div style={{ marginRight: 8 }}>
                      <SpaceDropdown
                        selectedSpaceId={selectedSpaceId}
                        onSelect={setSelectedSpaceId}
                      />
                    </div>
                  )}
                </HeadingRow>
                <AttachmentRowAnimator
                  visible={
                    pendingAttachments.length > 0 || selectedSkills.length > 0
                  }
                >
                  <AttachmentPreviewRow
                    style={{
                      flexWrap: "nowrap",
                      maxWidth: isMobile ? "100%" : WORKFLOW_INPUT_WIDTH,
                    }}
                  >
                    {selectedSkills.map((skill) => (
                      <AttachmentPreview
                        key={skill.id}
                        onPress={() => removeSkill(skill.id)}
                      >
                        <span className="file-icon">
                          <Icon name="graduation-cap" size={10} />
                        </span>
                        <span className="remove-icon">
                          <Icon name="x" size={10} />
                        </span>
                        <Text variant="text-small-md" ellipsis>
                          {skill.name}
                        </Text>
                      </AttachmentPreview>
                    ))}
                    {pendingAttachments.map((att) => (
                      <AttachmentPreview
                        key={att.id}
                        onPress={() => removeAttachment(att.id)}
                      >
                        <span className="file-icon">
                          <Icon
                            name={
                              att.mediaType === "application/pdf"
                                ? "file-scan"
                                : "paperclip"
                            }
                            size={10}
                          />
                        </span>
                        <span className="remove-icon">
                          <Icon name="x" size={10} />
                        </span>
                        <Text variant="text-small-md" ellipsis>
                          {att.fileName}
                        </Text>
                      </AttachmentPreview>
                    ))}
                  </AttachmentPreviewRow>
                </AttachmentRowAnimator>
                {isRecording ? (
                  <VoiceRecorderContainer
                    style={isMobile ? { width: "100%" } : undefined}
                  >
                    <VoiceRecorder
                      leftControls={
                        <PlanModeToggle
                          planMode={planMode}
                          onToggle={togglePlanMode}
                        />
                      }
                      onTranscript={(text) => {
                        const trimmed = text.trim();
                        setPrompt(trimmed);
                        requestAnimationFrame(() => {
                          const el = textareaRef.current;
                          if (el) {
                            el.focus();
                            el.setSelectionRange(
                              trimmed.length,
                              trimmed.length
                            );
                          }
                          scrollPromptToEnd();
                        });
                      }}
                      onCancel={() => {
                        setIsRecording(false);
                        setRecordingStartTime(null);
                      }}
                      onSend={(text) => {
                        setIsRecording(false);
                        setRecordingStartTime(null);
                        createWorkflow(text, true);
                      }}
                    />
                  </VoiceRecorderContainer>
                ) : (
                  <ComposerColumn
                    style={isMobile ? { width: "100%" } : undefined}
                  >
                    {showSuggestions ? (
                      <SlashMenu
                        onSelect={(p) => createWorkflow(p)}
                        onSelectSkill={(skill) => {
                          addSkill(skill);
                          requestAnimationFrame(() =>
                            textareaRef.current?.focus()
                          );
                        }}
                        onDismiss={() => setShowSuggestions(false)}
                        suggestions={gettingStartedSuggestions}
                        skills={availableSkills}
                        canCreateSkill={canCreateSkill}
                        onCreateSkill={() => {
                          const id = createSkill();
                          if (id) navigate(routes.modal.skill, { id });
                        }}
                      />
                    ) : (
                      <TextareaWrapper
                        data-strip-open={composerStripOpen}
                        style={{
                          width: isMobile ? "100%" : WORKFLOW_INPUT_WIDTH,
                        }}
                      >
                        <PromptTextarea
                          autoFocus={!isMobile}
                          ref={textareaRef}
                          value={prompt}
                          data-overflow={promptOverflow || undefined}
                          onChange={(e) => {
                            const shouldFollowCaret =
                              e.target.selectionStart ===
                                e.target.value.length &&
                              e.target.selectionEnd === e.target.value.length;
                            setPrompt(e.target.value);
                            syncSlashToken(e.target);
                            requestAnimationFrame(() => {
                              if (shouldFollowCaret) scrollPromptToEnd();
                              updatePromptOverflow();
                            });
                          }}
                          onScroll={updatePromptOverflow}
                          onKeyUp={(e) => syncSlashToken(e.currentTarget)}
                          onClick={(e) => syncSlashToken(e.currentTarget)}
                          onSelect={(e) => syncSlashToken(e.currentTarget)}
                          onFocus={() => setIsTextareaFocused(true)}
                          onBlur={() => setIsTextareaFocused(false)}
                          onPaste={handlePaste}
                          onKeyDown={(e) => {
                            if (handleSlashKeyDown(e)) return;
                            if (
                              e.key === "/" &&
                              !e.metaKey &&
                              !e.ctrlKey &&
                              prompt === "" &&
                              (gettingStartedSuggestions.items.length > 0 ||
                                availableSkills.length > 0)
                            ) {
                              e.preventDefault();
                              setShowSuggestions(true);
                              return;
                            }
                            if (
                              e.key === "Tab" &&
                              !prompt &&
                              placeholderPromptRef.current
                            ) {
                              e.preventDefault();
                              setPrompt(placeholderPromptRef.current);
                              requestAnimationFrame(scrollPromptToEnd);
                            } else if (
                              e.key === "Enter" &&
                              !e.shiftKey &&
                              !e.altKey &&
                              !e.ctrlKey
                            ) {
                              e.preventDefault();
                              createWorkflow(prompt);
                            }
                          }}
                          style={{
                            ...(isDragging && { color: "transparent" }),
                            ...(slashGhost !== "" && {
                              color: "transparent",
                              caretColor: "var(--text-primary)",
                            }),
                          }}
                        />
                        <GhostText
                          textareaRef={textareaRef}
                          value={prompt}
                          caret={slashCaret}
                          suffix={slashGhost}
                        />
                        {!isDragging && (
                          <PromptControls>
                            <PromptControlGroup>
                              <PlanModeToggle
                                planMode={planMode}
                                onToggle={togglePlanMode}
                              />
                            </PromptControlGroup>
                            <ComposerStripToggle />
                            <PromptControlGroup>
                              <Tooltip content="Voice mode">
                                <Button
                                  type="button"
                                  icon="mic"
                                  variant="secondary"
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => {
                                    setIsRecording(true);
                                    setRecordingStartTime(Date.now());
                                    setElapsedTime(0);
                                  }}
                                />
                              </Tooltip>
                              <Button
                                icon="arrow-up"
                                type="submit"
                                disabled={
                                  (!prompt.trim() &&
                                    pendingAttachments.length === 0 &&
                                    selectedSkills.length === 0) ||
                                  !selectedSpaceId
                                }
                              />
                            </PromptControlGroup>
                          </PromptControls>
                        )}
                        {isDragging && (
                          <Dropzone
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => e.preventDefault()}
                          >
                            <svg
                              width="100%"
                              height="100%"
                              style={{ position: "absolute", inset: 0 }}
                            >
                              <rect
                                x="1"
                                y="1"
                                width="calc(100% - 2px)"
                                height="calc(100% - 2px)"
                                rx="12"
                                ry="12"
                                fill="none"
                                stroke="var(--text-primary)"
                                strokeWidth="1"
                                strokeDasharray="6 4"
                                style={{
                                  animation: `${marchingDashes} 0.5s linear infinite`,
                                }}
                              />
                            </svg>
                            <Icon
                              name="arrow-down"
                              size={10}
                              color="var(--text-primary)"
                            />
                            <Text
                              variant="text-small"
                              color="var(--text-primary)"
                            >
                              Drop files here
                            </Text>
                          </Dropzone>
                        )}
                        <div
                          style={
                            isDragging ? { visibility: "hidden" } : undefined
                          }
                        >
                          <AnimatedPlaceholder
                            visible={!prompt}
                            onPromptChange={handlePlaceholderPromptChange}
                          />
                        </div>
                      </TextareaWrapper>
                    )}
                    <ComposerStrip
                      inset={false}
                      modelScope={{ type: "personal" }}
                      onAddFiles={processFiles}
                      effort={effort}
                      onEffortChange={setEffort}
                      chatModel={effectiveChatModel}
                      onChatModelChange={setChatModel}
                      deferModelQueries={
                        recentPresencesResult.type !== "complete"
                      }
                    />
                  </ComposerColumn>
                )}
                {importError && <ImportError>{importError}</ImportError>}
              </Form>
              {flyingFiles.map((f) => (
                <FlyingDot
                  key={f.id}
                  startX={f.x}
                  startY={f.y}
                  onDone={() => removeFlyingFile(f.id)}
                />
              ))}
            </CreateContainer>
          }
          tabHeader={
            <PagesTabHeader
              context={{ kind: "recents" }}
              workflowsCount={displayedWorkflows.length}
              linksCount={countPageCards(pagesRows)}
            />
          }
        >
          {isLinksTab ? (
            <PagesList
              rows={pagesRows}
              linksRoute={routes.recents.links}
              showSpaceColumn
              emptyTitle="No recent links"
              emptyDescription="Links you’ve opened appear here."
              isComplete={recentPagesResult.type === "complete"}
              recents
            />
          ) : (
            <ListTable
              columns={{ activity: "fixed-activity" }}
              headers={
                isMobile
                  ? { name: "Workflow" }
                  : {
                      name: "Workflow",
                      buildStatus: hasBuildStatus ? "3B status" : "",
                      space: "Space",
                      apps: "Connectors",
                      trigger: "Trigger",
                      createdAt: "Created",
                      createdBy: "Created by",
                      activity: "",
                      actions: "",
                    }
              }
              sortableColumns={isMobile ? [] : sortableColumns}
              dateColumns={isMobile ? [] : ["createdAt", "buildStatus"]}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
              footer={
                hasActiveFilters(filters) ? (
                  <ListFilterPills filters={filters} />
                ) : null
              }
              headerActions={<ListFilter filters={filters} />}
              emptyState={
                recentPresencesResult.type === "complete" ? (
                  recentWorkflows.length > 0 ? (
                    <EmptyState
                      title="No matches"
                      description="Try a different filter."
                      iconName="history"
                    />
                  ) : (
                    <EmptyState
                      title="No recent workflows"
                      description="Workflows you’ve viewed appear here."
                      iconName="history"
                    />
                  )
                ) : null
              }
              items={displayedWorkflows.map(({ presence, workflow, space }) => {
                return {
                  key: workflow.id,
                  link: makeUrl(routes.space.workflow, {
                    spaceId: space?.id ?? "",
                    workflowId: workflow.id,
                  }),
                  name: (hover) => (
                    <WorkflowNameCell
                      workflow={workflow}
                      hover={hover}
                      connectedAppName={
                        backingAppByWorkflowId.get(workflow.id)?.name
                      }
                    />
                  ),
                  buildStatus: (() => {
                    const branch = workflow.branches[0];
                    if (!branch || branch.name === "main") return null;
                    return (
                      <BuildStatus
                        status={getBranchListStatus(
                          branch,
                          presence.lastSeenAt
                        )}
                      />
                    );
                  })(),
                  space: space ? <SpaceBadge space={space} /> : null,
                  trigger: <TriggerTypes branches={workflow.branches} />,
                  apps: (
                    <ConnectorAvatars
                      connected={connectorAvatarItems(
                        getWorkflowConnectors(workflow),
                        connectorById
                      )}
                    />
                  ),
                  createdAt: (
                    <Text variant="text-small">
                      {new Date(workflow.createdAt).toLocaleDateString()}
                    </Text>
                  ),
                  createdBy: (
                    <Text
                      variant="text-small"
                      color="var(--text-primary)"
                      ellipsis
                    >
                      {workflow.createdBy
                        ? userDisplayName(workflow.createdBy)
                        : null}
                    </Text>
                  ),
                  activity: (
                    <ActivityFacepile
                      contributors={workflow.contributors}
                      presentUserIds={presentUserIdsByWorkflow.get(workflow.id)}
                    />
                  ),
                  actions: (hover) => {
                    const isFavorite = (workflow.favorites?.length ?? 0) > 0;
                    const workflowSpaceId = space?.id ?? "";
                    return (
                      <div style={{ opacity: hover ? 1 : 0 }}>
                        <WorkflowActionsMenu
                          workflowId={workflow.id}
                          spaceId={workflowSpaceId}
                          isFavorite={isFavorite}
                          autofixEnabled={workflow.autofixEnabled ?? true}
                          autoImproveEnabled={
                            workflow.autoImproveEnabled ?? false
                          }
                        />
                      </div>
                    );
                  },
                };
              })}
            />
          )}
        </DismissibleHeroLayout>
      </Backdrop>
    </>
  );
}
